import { useEffect, useState } from 'react';
import { Platform } from 'react-native';

/**
 * Visual Viewport Hook - Mobile Web keyboard fix (Chrome + Safari)
 * 
 * Returns both height and offsetTop using Visual Viewport API
 * to handle mobile browser keyboard interactions.
 * Sets CSS variables for dynamic positioning.
 */
export function useVisualViewport(): { height: number; offsetTop: number } {
  const [viewport, setViewport] = useState<{ height: number; offsetTop: number }>(() => {
    // Initialize with fallback for SSR or non-web platforms
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return { height: 0, offsetTop: 0 };
    }
    return {
      height: window.visualViewport?.height || window.innerHeight,
      offsetTop: window.visualViewport?.offsetTop || 0,
    };
  });

  useEffect(() => {
    // Only run on web platform
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }

    // Fallback to window.innerHeight if visualViewport not supported
    if (!window.visualViewport) {
      console.warn('Visual Viewport API not supported, using window.innerHeight fallback');
      const updateViewport = () => {
        const height = window.innerHeight;
        const offsetTop = 0; // No offsetTop in fallback mode
        setViewport({ height, offsetTop });
        
        // Update CSS custom properties
        document.documentElement.style.setProperty('--viewport-height', `${height}px`);
        document.documentElement.style.setProperty('--viewport-offset-top', '0px');
      };
      updateViewport();
      window.addEventListener('resize', updateViewport);
      return () => window.removeEventListener('resize', updateViewport);
    }

    const updateViewport = () => {
      const height = window.visualViewport!.height;
      const offsetTop = window.visualViewport!.offsetTop;
      
      setViewport({ height, offsetTop });

      // Update CSS custom properties for dynamic use across app
      document.documentElement.style.setProperty('--viewport-height', `${height}px`);
      document.documentElement.style.setProperty('--viewport-offset-top', `${Math.max(0, offsetTop)}px`);
    };

    // Initial update
    updateViewport();

    // Listen for viewport changes (keyboard open/close, browser chrome changes)
    window.visualViewport.addEventListener('resize', updateViewport);
    window.visualViewport.addEventListener('scroll', updateViewport);

    return () => {
      window.visualViewport?.removeEventListener('resize', updateViewport);
      window.visualViewport?.removeEventListener('scroll', updateViewport);
    };
  }, []);

  return viewport;
}
